import NoRecords from '../../../../shared/assets/icons/NoRecords';
import NoActiveMeters from '../../../../shared/assets/icons/NoActiveMeters';
import {useMemo} from 'react';
import {feedbackStateStyles} from './FeedbackState.styles';
import {Text, View} from 'react-native';
import {FeedbackStateProps, MessageType} from './FeedbackStateProps';
import UnableToFetch from '../../../../shared/assets/icons/UnableToFetch';
import SomethingWentWrong from '../../../../shared/assets/icons/SomethingWentWrong';
import {useScreenTheme} from '../../../../shared/hooks/useScreenTheme';
import {useTranslation} from 'react-i18next';
import UnableToLoad from '../../../../shared/assets/icons/UnableToLoad';
import NoRecordsSmall from '@shared/assets/icons/NoRecordsSmall';

const FeedbackState = (props: FeedbackStateProps) => {
  const {
    isFetchingError = false,
    isListEmpty = false,
    isNetworkError = false,
    unableToFetchShops = false,
    fromShopHome = false,
    fromRepairsList = false,
    fromWorkOrderSearch = false,
    customMesage = '',
    fromNotes = false,
    fromAttachments = false,
    customDesc = '',
    comingSoon = false,
    fromPartsList = false,
    fromMetersList = false,
    fromRepairHistory = false,
    isFromPartsSearch = false,
    customIcon,
    titleStyle,
    subTitleStyle,
  } = props;
  const theme = useScreenTheme('feedbackState');
  const styles = useMemo(() => feedbackStateStyles(theme), [theme]);
  const {t} = useTranslation();

  const showFeedback =
    isListEmpty ||
    isFetchingError ||
    isNetworkError ||
    unableToFetchShops ||
    comingSoon;

  const getText = (): MessageType => {
    let message: MessageType = {
      title: '',
      subTitle: '',
    };

    if (isListEmpty) {
      if (fromShopHome) {
        message.title = t('common.feedback.shopHomePage.noResults.title');
        message.subTitle = t('common.feedback.shopHomePage.noResults.subTitle');
      } else if (fromRepairsList) {
        message.title = t('common.feedback.repairsList.noResults.title');
        message.subTitle = t('common.feedback.repairsList.noResults.subTitle');
      } else if (fromWorkOrderSearch) {
        message.title = t('common.feedback.workOrderSearch.noResults.title');
        message.subTitle = t(
          'common.feedback.workOrderSearch.noResults.subTitle',
        );
      } else if (fromNotes || fromAttachments) {
        message.title = customMesage;
        message.subTitle = customDesc;
      } else if (fromPartsList) {
        message.title = t('common.feedback.partsList.noResults.title');
        message.subTitle = t('common.feedback.partsList.noResults.subTitle');
      } else if (fromMetersList) {
        message.title = t('repairDetails.meterManagement.noActiveMeters');
        message.subTitle = '';
      } else {
        message.title = t('common.feedback.noResults.title');
        message.subTitle = t('common.feedback.noResults.subTitle');
      }
    } else if (isFetchingError) {
      if (isFromPartsSearch) {
        message.title = t('repairDetails.searchPartsModal.errorTitle');
        message.subTitle = t('repairDetails.searchPartsModal.errorDesc');
      } else {
        message.title = t('common.feedback.unableToFetch.title');
        message.subTitle = t('common.feedback.unableToFetch.subTitle');
      }
    } else if (isNetworkError) {
      message.title = t('common.feedback.networkError.title');
      message.subTitle = t('common.feedback.networkError.subTitle');
    } else if (unableToFetchShops) {
      message.title = t('common.feedback.unableToLoad.title');
      message.subTitle = t('common.feedback.unableToLoad.subTitle');
    } else if (comingSoon) {
      message.title = t('common.feedback.comingSoon.title');
      message.subTitle = t('common.feedback.comingSoon.subTitle');
    }

    return message;
  };

  if (!showFeedback) {
    return null;
  }

  const text = getText();

  const titleText =
    typeof text?.title === 'string' && text.title.length > 0
      ? text.title.replace(/\b\w/g, char => char.toUpperCase()) // NOSONAR
      : String(text?.title ?? '');

  return (
    <View
      style={styles.container}
      accessible={false}
      importantForAccessibility="no"
      // @ts-ignore — aria-live is a web-only prop supported by React Native Web
      aria-live="polite">
      <View style={styles.subContainer}>
        {isListEmpty && !fromMetersList && !fromRepairHistory && (
          <NoRecords
            bgColor={theme.noRecordsBgColor}
            shadowColor={theme.noRecordsShadowColor}
            color={theme.noRecordsDocColor}
            circleColor={theme.noRecordsCircleColor}
            borderColor={theme.noRecordsBorderColor}
          />
        )}
        {isListEmpty && fromMetersList && (
          <NoActiveMeters
            bgColor={theme.noRecordsBgColor}
            shadowColor={theme.noRecordsShadowColor}
            color={theme.noRecordsDocColor}
            borderColor={theme.noRecordsBorderColor}
          />
        )}
        {isListEmpty && fromRepairHistory && (
          <NoRecordsSmall
            bgColor={theme.noRecordsBgColor}
            shadowColor={theme.noRecordsShadowColor}
            color={theme.noRecordsDocColor}
            circleColor={theme.noRecordsCircleColor}
            borderColor={theme.noRecordsBorderColor}
          />
        )}
        {isFetchingError && (customIcon ?? <UnableToFetch />)}
        {isNetworkError && <SomethingWentWrong />}
        {unableToFetchShops && <UnableToLoad />}
        {comingSoon && <SomethingWentWrong />}
        <View style={styles.titleContainer}>
          <Text
            style={[styles.title, titleStyle]}
            accessible={true}
            accessibilityRole="header"
            accessibilityLabel={titleText}
            // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
            tabIndex={0}
            focusable={true}>
            {titleText}
          </Text>
          <Text
            style={[styles.subTitle, subTitleStyle]}
            accessible={true}
            accessibilityLabel={text?.subTitle}
            // @ts-ignore — tabIndex and focusable are required for Tab-key focus on web
            tabIndex={0}
            focusable={true}>
            {text?.subTitle}
          </Text>
        </View>
      </View>
    </View>
  );
};

export default FeedbackState;
